import {COMPANY_KEYS, useUpdateCreditsCache} from "@/api/companyApi";
import {stripParamKeys} from "@/api/influencerApi";
import {USER_KEYS} from "@/api/userApi";
import type {
  CampaignFeedSerializer,
  CampaignGroupInviteSerializer,
  CampaignManagementSerializer,
  CampaignManagementStatsSerializer,
  CampaignOwnerDetailSerializer,
  CampaignReportingSerializer,
  CreativeSnippetsResponseSerializer,
  FeaturedContentSerializer,
  InfluencerRatingSerializer,
  InstagramMediaResponseSerializer,
  InviteInfluencerSerializer,
  OpenCampaignSerializer,
  PromotionCampaignAssetSerializer,
} from "@/generated/DjangoTypes";
import {useSliceInfluencerQuery} from "@/hooks/useInfluencer";
import {categoryUrlParts} from "@/hooks/useInviteCategories";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useGenericInfiniteQuery, useGenericInfiniteQueryWithCount, useGenericQuery} from "@/hooks/useQueryHooks";
import {useIsAdmin, useSelectedCompanyId} from "@/hooks/useUserHooks";
import {extractCutoffFromPageParams, sumCountsByCutoffGroup} from "@/hooks/useVibeSearchBroadening";
import {client} from "@/httpClient";
import {
  useCampaignFormState,
  useComposedCampaignState,
  useMiscActions,
  useSetCampaignFormState,
} from "@/store/campaignForm";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import useCampaignManagementStore, {useManagementCampaign} from "@/store/campaignManagement";
import {hidePublishShareNudgeAtom, publishShareNudgeModalAtom} from "@/store/jotaiAtoms";
import type {
  TCampaignApplicationAccepted,
  TCampaignApplicationCounts,
  TCampaignApplicationOpen,
  TCampaignApplicationReporting,
  TInfiniteQueryOptions,
  TInviteCategory,
  TMediaGalleryItem,
  TPaginated,
  TQueryOptions,
  TRestructurableCampaignField,
  TVibePaginated,
  TVibeQueryResult,
} from "@/store/types";
import useUserStore from "@/store/user";
import type {TContentApprovalMode} from "@/utils/contentApprovalMode";
import type {TError} from "@/utils/errorHandlers";
import {useMutation, useQueryClient, type InfiniteData, type QueryKey} from "@tanstack/react-query";
import {useNavigate} from "@tanstack/react-router";
import type {AxiosError, AxiosResponse} from "axios";
import {produce} from "immer";
import {getDefaultStore} from "jotai";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

export const CAMPAIGN_KEYS = {
  mAiCampaignCreation: ["/ai-campaign-creation/"],
  mApplyHint: ["/apply-hint/"],
  mCampaign: (campaignId: number) => [`/campaign/${campaignId}/`],
  mCampaignApplyCreativeSnippet: (campaignId: number) => [`/campaign/${campaignId}/apply-creative-snippet/`],
  mCampaignAssets: (campaignId: number) => [`/campaign/${campaignId}/assets/`],
  mCampaignBulkInvite: (campaignId: number) => [`/campaign/${campaignId}/bulk_invite/`],
  mCampaignBulkUploadApplicationsCsv: (campaignId: number) => [`/campaign/${campaignId}/bulk_upload_applications_csv/`],
  mCampaignCreation: (campaignId: number) => [`/campaign/${campaignId}/create-campaign/`],
  mCampaignDelete: (campaignId: number) => [`/campaign/${campaignId}/delete/`],
  mCampaignDownloadReportPdf: (campaignId: number) => [`/campaign/${campaignId}/download_report_pdf/`],
  mCampaignDuplicate: (campaignId: number) => [`/campaign/${campaignId}/duplicate/`],
  mCampaignExtendDates: (campaignId: number) => [`/campaign/${campaignId}/extend_dates/`],
  mCampaignFieldRestructure: (campaignId: number) => [`/campaign/${campaignId}/restructure-field/`],
  mCampaignImageDeletion: (campaignId: number) => [`/campaign/${campaignId}/delete_image/`],
  mCampaignInfluencerRating: (campaignId: number) => [`/campaign/${campaignId}/rate_influencer/`],
  mCampaignInvite: (campaignId: number) => [`/campaign/${campaignId}/invite/`],
  mCampaignList: ["/campaign/"],
  mCampaignOngoingComplete: (campaignId: number) => [`/campaign/${campaignId}/complete_ongoing_campaign/`],
  mCampaignOngoingPause: (campaignId: number) => [`/campaign/${campaignId}/pause_ongoing_campaign/`],
  mCampaignOngoingReport: (campaignId: number) => [`/campaign/${campaignId}/submit_ongoing_report/`],
  mCampaignOngoingUnpause: (campaignId: number) => [`/campaign/${campaignId}/unpause_ongoing_campaign/`],
  mCampaignReactivateApplications: (campaignId: number) => [`/campaign/${campaignId}/reactivate_applications/`],
  mCampaignReorderImage: (campaignId: number) => [`campaign/${campaignId}/reorder_image/`],
  mCampaignReport: (campaignId: number) => [`/campaign/${campaignId}/submit_report/`],
  mCampaignSetContentApproval: (campaignId: number) => [`/campaign/${campaignId}/set_content_approval/`],
  mCampaignSortCreativeSnippets: (campaignId: number) => [`/campaign/${campaignId}/sort-creative-snippets/`],
  mCampaignStart: (campaignId: number) => [`/campaign/${campaignId}/start_campaign/`],
  mCampaignTypeSwitch: (campaignId: number) => [`/campaign/${campaignId}/switch/`],
  mCampaignUgcContent: (campaignId: number) => [`/campaign/${campaignId}/create_ugc_content/`],
  mCampaignUgcContentRemoval: ["/campaign/delete_ugc_content/"],
  mCampaignUgcDetailsUpdate: ["/campaign/update_ugc_details/"],
  mExtractDocumentToPrompt: ["/extract_document_to_prompt/"],
  mHintProvider: ["/hint-provider/"],
  mMarkAllProductsSent: (campaignId: number) => [`/campaign/${campaignId}/mark_all_products_sent/`],
  mUploadAiCampaignAssets: (campaignId: number) => [`/campaign/${campaignId}/upload_ai_assets/`],
  mVoiceTranscription: ["/transcribe_voice/"],
  qCampaign: (campaignId: number) => [`/campaign/${campaignId}/`],
  qCampaignAcceptedApplications: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/accepted_applications/?${params}`,
  ],
  qCampaignAcceptedApplicationsCsv: (campaignId: number) => [`/campaign/${campaignId}/accepted_applications_csv/`],
  qCampaignApplicationCounts: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/application_counts/?${params}`,
  ],
  qCampaignActiveApplications: (campaignId: number, searchParam?: string) => [
    `/campaign/${campaignId}/active_applications/?search=${searchParam}`,
  ],
  qCampaignAssets: (campaignId: number) => [`/campaign/${campaignId}/assets/`],
  qCampaignCancelledApplications: (campaignId: number, searchParam?: string) => [
    `/campaign/${campaignId}/cancelled_applications/?search=${searchParam}`,
  ],
  qCampaignDeclinedApplications: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/declined_applications/?${params}`,
  ],
  qCampaignEdit: (campaignId: number) => [`/campaign/${campaignId}/edit/`],
  qCampaignFeaturedContent: (campaignId: number, date?: string | null) => [
    `/campaign/${campaignId}/featured_content/${date ? `?date=${date}` : ""}`,
  ],
  qCampaignGallery: (campaignId: number, searchParams?: string | null) => [
    `/campaign/${campaignId}/media_gallery/${searchParams ? `?${searchParams}` : ""}`,
  ],
  qCampaignInvitableInfluencerFilters: (campaignId: number) => [`/campaign/${campaignId}/invite_influencers_data/`],
  qCampaignInvitableInfluencers: ({
    campaignId,
    category,
    searchParams,
  }: {
    campaignId: number;
    category: TInviteCategory;
    searchParams?: string | null;
  }) => [`/campaign/${campaignId}/${categoryUrlParts[category]}/${searchParams ? `?${searchParams}` : ""}`],
  qCampaignInvitations: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/pending_invitations/?${params}`,
  ],
  qCampaignInvitedGroups: (campaignId: number) => [`/campaign/${campaignId}/groups_invited/`],
  qCampaignList: (params?: string) => [`/campaign/?apply=true&order=True&${params}`],
  qCampaignPlatformCounts: (params?: string) => [`/campaign/platform_counts/?apply=true&order=True&${params}`],
  qCampaignManagement: (campaignId: number) => [`/campaign/${campaignId}/management/`],
  qCampaignManagementStats: (campaignId: number) => [`/campaign/${campaignId}/management_stats/`],
  qCampaignOpenApplications: (campaignId: number, params?: string) => [
    `/campaign/${campaignId}/open_applications/?${params}`,
  ],
  qCampaignParticipants: (campaignId: number) => [`/campaign/${campaignId}/participants/`],
  qCampaignPreparationApplications: (campaignId: number, isPending: boolean = false, searchParam?: string) => [
    `/campaign/${campaignId}/preparation_applications/?pending=${isPending}&search=${searchParam}`,
  ],
  qCampaignPriceSummary: (campaignId: number, invoiceId?: number) => [
    `/campaign/${campaignId}/price_summary/${invoiceId ? `?invoice_id=${invoiceId}` : ""}`,
  ],
  qCampaignRenegotiations: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/renegotiations/?${params}`,
  ],
  qCampaignRejectedInvitations: (campaignId: number, params?: string | null) => [
    `/campaign/${campaignId}/rejected_invitations/?${params}`,
  ],
  qCampaignReporting: ({campaignId, dateFilter}: {campaignId: number; dateFilter?: string | null}) => [
    `/campaign/${campaignId}/reporting/${dateFilter ? `?date=${dateFilter}` : ""}`,
  ],
  qCampaignReportingApplications: ({
    campaignId,
    dateFilter,
    ordering,
    searchParam,
  }: {
    campaignId: number;
    dateFilter?: string | null;
    ordering?: string | null;
    searchParam?: string;
  }) => [
    `/campaign/${campaignId}/reporting_accepted_applications/?${dateFilter ? `date=${dateFilter}&` : ""}${ordering ? `ordering=${ordering}&` : ""}search=${searchParam}`,
  ],
  qDefaultCreativeSnippets: ["/creative-snippets/default/"],
  qOpenCampaigns: ["/campaign/open_campaigns/"],
} as const;

export const useApplyCreativeSnippetMutation = ({
  campaignId,
  onSuccess,
  onError,
}: {
  campaignId: number;
  onSuccess?: (
    data: {creative_snippet_sentence: string},
    variables: {
      campaignId: number;
      snippetType: "content_format" | "mood_style";
      snippetUuid: string;
      languageCode: string;
      campaignDescription: string;
      taskDescription: string;
      brandDoes: string;
      dontsDescription: string;
    },
  ) => void;
  onError?: (error: AxiosError) => void;
}) => {
  return useMutation<
    {creative_snippet_sentence: string},
    AxiosError,
    {
      campaignId: number;
      snippetType: "content_format" | "mood_style";
      snippetUuid: string;
      languageCode: string;
      campaignDescription: string;
      taskDescription: string;
      brandDoes: string;
      dontsDescription: string;
    }
  >({
    meta: {skipSuccessToast: true},
    mutationFn: async ({
      campaignId,
      snippetType,
      snippetUuid,
      languageCode,
      campaignDescription,
      taskDescription,
      brandDoes,
      dontsDescription,
    }) => {
      const response = await client.post(CAMPAIGN_KEYS.mCampaignApplyCreativeSnippet(campaignId)[0], {
        snippet_type: snippetType,
        snippet_uuid: snippetUuid,
        language_code: languageCode,
        campaign_description: campaignDescription,
        task_description: taskDescription,
        brand_does: brandDoes,
        donts_description: dontsDescription,
      });
      return response.data;
    },
    mutationKey: CAMPAIGN_KEYS.mCampaignApplyCreativeSnippet(campaignId),
    onSuccess,
    onError,
  });
};

export const useApplyHintMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: AxiosResponse<{improved_text: string}>) => void;
  onError?: (error: AxiosError) => void;
} = {}) => {
  return useMutation<
    AxiosResponse<{improved_text: string}>,
    AxiosError,
    {mainPromptText: string; userInput: string; hintKey: string; languageCode: string}
  >({
    meta: {skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mApplyHint,
    mutationFn: async ({mainPromptText, userInput, hintKey, languageCode}) => {
      const response = await client.post(CAMPAIGN_KEYS.mApplyHint[0], {
        main_prompt_text: mainPromptText,
        user_input: userInput,
        hint_key: hintKey,
        user_language_code: languageCode,
      });
      return response;
    },
    onSuccess,
    onError,
  });
};

type TAiCampaignCreationVariables = {
  campaignId: number;
  files?: File[];
  languageCode: string;
  mainPromptText: string;
  tiktokTrackedKeywords?: string[];
};

export const useAiCampaignCreationMutation = () => {
  return useMutation<
    AxiosResponse<{
      compensation_type: string;
      saved_to_database: boolean;
      formatting_applied: boolean;
    }>,
    AxiosError,
    TAiCampaignCreationVariables
  >({
    meta: {skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mAiCampaignCreation,
    mutationFn: async ({campaignId, mainPromptText, languageCode, files, tiktokTrackedKeywords}) => {
      const formData = new FormData();
      formData.append("main_prompt_text", mainPromptText);
      formData.append("language_code", languageCode);
      if (files) {
        for (const file of files) {
          formData.append("files", file);
        }
      }

      if (tiktokTrackedKeywords) {
        for (const keywordId of tiktokTrackedKeywords) {
          formData.append("tiktok_tracked_keywords", keywordId);
        }
      }

      const response = await client.post(CAMPAIGN_KEYS.mCampaignCreation(campaignId)[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
      return response;
    },
  });
};

export const useUploadAiCampaignAssetsMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (
    response: AxiosResponse<{created_asset_ids: number[]; count: number}>,
    variables: {campaignId: number; files: File[]},
  ) => void;
  onError?: (error: AxiosError) => void;
} = {}) => {
  return useMutation<
    AxiosResponse<{created_asset_ids: number[]; count: number}>,
    AxiosError,
    {campaignId: number; files: File[]}
  >({
    meta: {skipSuccessToast: true},
    mutationFn: async ({campaignId, files}) => {
      const formData = new FormData();
      for (const file of files) {
        formData.append("files", file);
      }
      const response = await client.post(CAMPAIGN_KEYS.mUploadAiCampaignAssets(campaignId)[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
      return response;
    },
    onSuccess,
    onError,
  });
};

export type TBulkUploadApplicationsCsvBucket = {
  assigned_count: number;
  assigned: string[];
  changed_count?: number;
  changed?: string[];
  skipped_already_assigned_count: number;
  skipped_already_assigned: string[];
  skipped_empty_count: number;
  skipped_empty: string[];
};

export type TBulkUploadApplicationsCsvResponse = {
  community_codes: TBulkUploadApplicationsCsvBucket;
  link_stickers: TBulkUploadApplicationsCsvBucket;
  not_found_count: number;
  not_found: string[];
  voucher_codes: TBulkUploadApplicationsCsvBucket;
};

export const useBulkUploadApplicationsCsvMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (
    response: AxiosResponse<TBulkUploadApplicationsCsvResponse>,
    variables: {campaignId: number; file: File},
  ) => void;
  onError?: (error: AxiosError) => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<TBulkUploadApplicationsCsvResponse>, AxiosError, {campaignId: number; file: File}>({
    meta: {skipSuccessToast: true},
    mutationFn: async ({campaignId, file}) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await client.post(CAMPAIGN_KEYS.mCampaignBulkUploadApplicationsCsv(campaignId)[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
      return response;
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({queryKey: CAMPAIGN_KEYS.qCampaignAcceptedApplicationsCsv(variables.campaignId)});
      queryClient.invalidateQueries({queryKey: CAMPAIGN_KEYS.qCampaignManagement(variables.campaignId)});
      queryClient.invalidateQueries({
        predicate: query =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].includes(`/campaign/${variables.campaignId}/preparation_applications/`),
      });
      onSuccess?.(response, variables);
    },
    onError,
  });
};

export const useHintProviderMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (
    response: AxiosResponse<{
      hint_status: {
        key: string;
        completed: boolean;
        name_en?: string;
        name_de?: string;
        description_short_en?: string;
        description_short_de?: string;
      }[];
    }>,
    variables: {currentText: string; hintsStatus: {key: string; completed: boolean}[]; initialHintResponse: boolean},
  ) => void;
  onError?: (error: AxiosError) => void;
} = {}) => {
  return useMutation<
    AxiosResponse<{
      hint_status: {
        key: string;
        completed: boolean;
        name_en?: string;
        name_de?: string;
        description_short_en?: string;
        description_short_de?: string;
      }[];
    }>,
    AxiosError,
    {currentText: string; hintsStatus: {key: string; completed: boolean}[]; initialHintResponse: boolean}
  >({
    meta: {skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mHintProvider,
    mutationFn: async ({currentText, hintsStatus, initialHintResponse}) => {
      const response = await client.post(CAMPAIGN_KEYS.mHintProvider[0], {
        current_text: currentText,
        hints_status: hintsStatus,
        initial_hint_response: initialHintResponse,
      });
      return response;
    },
    onSuccess,
    onError,
  });
};

export const useExtractDocumentToPromptMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (
    response: AxiosResponse<{improved_prompt: string}>,
    variables: {currentPrompt: string; languageCode: string; file: File},
  ) => void;
  onError?: (error: AxiosError) => void;
} = {}) => {
  return useMutation<
    AxiosResponse<{improved_prompt: string}>,
    AxiosError,
    {currentPrompt: string; languageCode: string; file: File}
  >({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mExtractDocumentToPrompt,
    mutationFn: async ({currentPrompt, languageCode, file}) => {
      const formData = new FormData();
      formData.append("current_prompt", currentPrompt);
      formData.append("language_code", languageCode);
      formData.append("file", file);
      const response = await client.post(CAMPAIGN_KEYS.mExtractDocumentToPrompt[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
      return response;
    },
    onSuccess,
    onError,
  });
};

export const usePublishCampaignMutation = () => {
  const {t} = useTranslation("campaign_creation");
  const navigate = useNavigate();
  const actions = useMiscActions();
  const campaign = useComposedCampaignState(campaign => campaign);
  const errors = useCampaignFormState(state => state.errors);
  const queryClient = useQueryClient();
  const setFormState = useSetCampaignFormState();
  const user = useUserStore(state => state.user);

  return useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationFn: ({isDraft}: {isDraft: boolean}) => actions.saveCampaign(isDraft),
    onSuccess: (response, {isDraft}) => {
      const wasFreshPublish = !isDraft && (campaign.misc.status === "draft" || campaign.misc.status === "planned");
      const companyId = campaign.misc.companyId;

      actions.resetStore();
      [
        COMPANY_KEYS.qCompanyCampaigns().slice(0, -1),
        CAMPAIGN_KEYS.qCampaign(response.data.id),
        USER_KEYS.qUser(Number(user.id)),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));

      navigate({
        to: "/campaign/$campaignId",
        params: {campaignId: response.data.id},
        search: wasFreshPublish ? {show_survey: true} : undefined,
      });
      window.scrollTo(0, 0);

      if (wasFreshPublish && companyId) {
        const store = getDefaultStore();
        if (!store.get(hidePublishShareNudgeAtom)[Number(user.id)]) {
          store.set(publishShareNudgeModalAtom, draft => {
            draft.companyId = Number(companyId);
            draft.isOpen = true;
          });
        }
      }
    },
    onError: (error: TError) => {
      if (error.response?.data.detail === "no_active_subscription") {
        navigate({
          to: "/plans",
          search: () => ({
            campaign_id: campaign.misc.id,
            campaign_size: campaign.socials.campaignSize,
            pro: campaign.misc.isProInfluencer,
            type: campaign.misc.campaignType as "instagram",
          }),
        });
      } else {
        const responseData = error.response?.data;
        const newErrors: string[] = [];

        if (responseData) {
          for (const key in responseData) {
            if (Array.isArray(responseData[key])) {
              newErrors.push(
                `${t(key as "campaign_title_required")} - ${responseData[key].map(detail => `${t(detail as "campaign_title_required")} `)}`,
              );
            } else if (typeof responseData[key] === "string") {
              newErrors.push(responseData[key]);
            } else if (
              responseData[key] &&
              typeof responseData[key] === "object" &&
              "detail" in (responseData[key] as object)
            ) {
              newErrors.push(`errors:${(responseData[key] as {detail: string}).detail}`);
            }
          }
        }

        setFormState("errors", [...errors, ...newErrors]);
      }
    },
  });
};

export const useRestructureCampaignFieldMutation = ({
  campaignId,
  currentText,
  fieldType,
  onSuccess,
}: {
  campaignId: number;
  currentText: string;
  fieldType: TRestructurableCampaignField;
  onSuccess?: (response: AxiosResponse<{restructured_text: string}>) => void;
}) =>
  useMutation<AxiosResponse<{restructured_text: string}>, TError>({
    meta: {skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mCampaignFieldRestructure(campaignId),
    mutationFn: () =>
      client.post(CAMPAIGN_KEYS.mCampaignFieldRestructure(campaignId)[0], {
        current_text: currentText,
        field_type: fieldType,
      }),
    onSuccess,
  });

export const useSortCreativeSnippetsMutation = ({
  campaignId,
  onSuccess,
  onError,
}: {
  campaignId: number;
  onSuccess?: (data: CreativeSnippetsResponseSerializer) => void;
  onError?: (error: AxiosError) => void;
}) =>
  useMutation<CreativeSnippetsResponseSerializer, AxiosError>({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mCampaignSortCreativeSnippets(campaignId),
    mutationFn: async () => {
      const response = await client.post(CAMPAIGN_KEYS.mCampaignSortCreativeSnippets(campaignId)[0]);
      return response.data;
    },
    onSuccess,
    onError,
  });

export const useStartCampaignMutation = (applicationId?: number, creditsApplied?: number) => {
  const {influencerLimit} = usePackageLimits();
  const campaign = useManagementCampaign();
  const startCampaign = useCampaignManagementStore(state => state.startCampaign);
  const {data: stats} = useCampaignManagementStatsQuery({campaignId: campaign.id});
  const updateCreditsCache = useUpdateCreditsCache();

  return useMutation({
    meta: {errorCount: influencerLimit, skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mCampaignStart(campaign.id),
    mutationFn: () => startCampaign(applicationId),
    onSuccess: () => {
      const credits = creditsApplied ?? stats?.credits_applied;

      if (credits) {
        updateCreditsCache(-credits);
      }
    },
  });
};

export const useCampaignInfluencerRatingMutation = ({
  applicationId,
  campaignId: campaignIdOverride,
  onSuccess,
  rating,
  userId,
  onError,
}: {
  applicationId?: number;
  campaignId?: number;
  onSuccess?: () => void;
  rating?: InfluencerRatingSerializer;
  userId?: number;
  onError?: (error: AxiosError) => void;
}) => {
  const {t} = useTranslation("common");
  const campaign = useManagementCampaign();
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);
  const ordering = useCampaignManagementStore(state => state.reportingOrdering);
  const search = useCampaignInfluencerFilterStore(state => state.filters.search);
  const queryClient = useQueryClient();
  const campaignId = campaignIdOverride ?? campaign.id;

  const {mutate: rateInfluencer, isPending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mCampaignInfluencerRating(campaignId),
    mutationFn: ({
      comment,
      contentQuality,
      reliability,
    }: {
      comment: string;
      contentQuality: number;
      reliability: number;
    }) =>
      client.post(CAMPAIGN_KEYS.mCampaignInfluencerRating(campaignId)[0], {
        comment: comment.trim(),
        reliability: reliability,
        quality: contentQuality,
        rating_for: userId,
      }),
    onError,
    onSuccess: async (_, {comment, contentQuality, reliability}) => {
      queryClient.setQueryData<AxiosResponse<CampaignReportingSerializer>>(
        CAMPAIGN_KEYS.qCampaignReporting({campaignId, dateFilter}),
        prevState =>
          prevState &&
          produce(prevState, draft => {
            if (typeof draft.data.influencers_rated !== "undefined" && !rating) {
              draft.data.influencers_rated += 1;
            }
          }),
      );

      queryClient.setQueryData<InfiniteData<AxiosResponse<TPaginated<TCampaignApplicationAccepted>>>>(
        CAMPAIGN_KEYS.qCampaignReportingApplications({campaignId, dateFilter, ordering, searchParam: search}),
        prevState =>
          produce(prevState, draft => {
            draft?.pages.forEach(page => {
              page.data.results.forEach(application => {
                if (application.id === applicationId) {
                  if (!application.influencer_rating) {
                    application.influencer_rating = {
                      campaign: campaignId,
                      id: 0,
                      rating_by: 0,
                    };
                  }

                  application.influencer_rating.comment = comment.trim() || null;
                  application.influencer_rating.quality = contentQuality;
                  application.influencer_rating.reliability = reliability;
                  application.rated_influencer = true;
                }
              });
            });
          }),
      );

      const markRated = (cachedApplication?: {id: number; rated_influencer?: boolean} | null) => {
        if (cachedApplication && cachedApplication.id === applicationId) {
          cachedApplication.rated_influencer = true;
        }
      };

      queryClient.setQueriesData<
        InfiniteData<
          AxiosResponse<TPaginated<{campaign_application?: {id: number; rated_influencer?: boolean} | null}>>
        >
      >(
        {predicate: query => String(query.queryKey[0]).startsWith("/media_library/?")},
        prevState =>
          prevState &&
          produce(prevState, draft => {
            draft.pages.forEach(page => page.data.results.forEach(media => markRated(media.campaign_application)));
          }),
      );

      queryClient.setQueriesData<InfiniteData<AxiosResponse<TPaginated<TMediaGalleryItem>>>>(
        {predicate: query => String(query.queryKey[0]).startsWith(`/campaign/${campaignId}/media_gallery/`)},
        prevState =>
          prevState &&
          produce(prevState, draft => {
            draft.pages.forEach(page => page.data.results.forEach(item => markRated(item.result.campaign_application)));
          }),
      );

      toast.success(t("toast_global_success"));
      await queryClient.invalidateQueries({queryKey: CAMPAIGN_KEYS.qCampaignManagement(campaignId)});
      onSuccess?.();
    },
  });

  return {mutate: rateInfluencer, isPending};
};

export const useSetContentApprovalMutation = (campaignId: number) => {
  const {t} = useTranslation("content_approval");
  const queryClient = useQueryClient();

  const {mutate, isPending} = useMutation<
    {queued_submissions?: number; reverted_applications?: number},
    AxiosError,
    {mode: TContentApprovalMode}
  >({
    meta: {skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mCampaignSetContentApproval(campaignId),
    mutationFn: async ({mode}) => {
      const response = await client.post(CAMPAIGN_KEYS.mCampaignSetContentApproval(campaignId)[0], {mode});
      return response.data;
    },
    onSuccess: async data => {
      if (data.queued_submissions !== undefined) {
        toast.success(t("switch_to_ai_success", {count: data.queued_submissions}));
      } else if (data.reverted_applications !== undefined) {
        toast.success(t("revert_from_ai_success", {count: data.reverted_applications}));
      } else {
        toast.success(t("content_approval_updated"));
      }
      await queryClient.invalidateQueries({queryKey: CAMPAIGN_KEYS.qCampaign(campaignId)});
      await queryClient.invalidateQueries({queryKey: CAMPAIGN_KEYS.qCampaignEdit(campaignId)});
      await queryClient.invalidateQueries({queryKey: CAMPAIGN_KEYS.qCampaignManagement(campaignId)});
    },
  });

  return {setContentApproval: mutate, isPending};
};

export const useMarkAllProductsSentMutation = (campaignId: number) => {
  const {t} = useTranslation("campaign_preparation");
  const queryClient = useQueryClient();

  const {mutate, isPending} = useMutation({
    mutationKey: CAMPAIGN_KEYS.mMarkAllProductsSent(campaignId),
    mutationFn: async () => {
      const response = await client.post(CAMPAIGN_KEYS.mMarkAllProductsSent(campaignId)[0]);
      return response.data;
    },
    onSuccess: async () => {
      toast.success(t("all_packages_marked_shipped"));
      await queryClient.invalidateQueries({
        predicate: query =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith(`/campaign/${campaignId}/preparation_applications/`),
      });
      await queryClient.invalidateQueries({queryKey: CAMPAIGN_KEYS.qCampaignManagement(campaignId)});
    },
  });

  return {markAllProductsSent: mutate, isPending};
};

export const useCampaignQuery = ({
  campaignId,
  queryOptions,
}: {
  queryOptions?: TQueryOptions<CampaignOwnerDetailSerializer>;
  campaignId: number;
}) =>
  useGenericQuery<CampaignOwnerDetailSerializer>({
    queryKey: CAMPAIGN_KEYS.qCampaign(campaignId),
    enabled: !!campaignId,
    ...queryOptions,
  });

export const useCampaignAcceptedApplicationsQuery = ({
  campaignId,
  isActiveTab,
  isPro = false,
}: {
  campaignId: number;
  isActiveTab?: boolean;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useCampaignInfluencerFilterStore(state => state.searchParams);

  return useCampaignInfluencerInfiniteQuery<TCampaignApplicationOpen>({
    enabled: !!campaignId && (isPro ? !!management.accepted_applications_pro : !!management.accepted_applications),
    isActiveTab,
    queryKey: CAMPAIGN_KEYS.qCampaignAcceptedApplications(
      campaignId,
      `${stripParamKeys(searchParams ?? "")}&pro=${isPro}`,
    ),
  });
};

export const useCampaignActiveApplicationsQuery = ({
  campaignId,
  searchParams,
  queryOptions,
}: {
  campaignId: number;
  searchParams?: string;
  queryOptions?: TInfiniteQueryOptions<TCampaignApplicationAccepted>;
}) =>
  useGenericInfiniteQuery<TCampaignApplicationAccepted>({
    queryKey: CAMPAIGN_KEYS.qCampaignActiveApplications(campaignId, searchParams),
    enabled: !!campaignId,
    select: data =>
      data.pages.flatMap(page =>
        page.data.results.map(application => ({
          ...application,
          content_submissions: applyFirstCarouselChildMedia(application.content_submissions, s => s.carousel_medias),
          instagram_media: {
            ...application.instagram_media,
            posts: applyFirstCarouselChildMedia(application.instagram_media?.posts, p => p.children) ?? [],
          } as InstagramMediaResponseSerializer,
        })),
      ),
    ...queryOptions,
  });

export const useCampaignApplicationCountsQuery = ({
  campaignId,
  queryOptions,
  searchParams,
}: {
  campaignId: number;
  queryOptions?: TQueryOptions<TCampaignApplicationCounts>;
  searchParams?: string | null;
}) =>
  useGenericQuery<TCampaignApplicationCounts>({
    queryKey: CAMPAIGN_KEYS.qCampaignApplicationCounts(campaignId, searchParams),
    enabled: !!campaignId,
    ...queryOptions,
  });

export const useCampaignAssetsQuery = ({campaignId}: {campaignId: number}) =>
  useGenericQuery<PromotionCampaignAssetSerializer[]>({
    queryKey: CAMPAIGN_KEYS.qCampaignAssets(campaignId),
    enabled: !!campaignId,
  });

export const useCampaignDeclinedApplicationsQuery = ({
  campaignId,
  isActiveTab,
  isPro = false,
}: {
  campaignId: number;
  isActiveTab?: boolean;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useCampaignInfluencerFilterStore(state => state.searchParams);

  return useCampaignInfluencerInfiniteQuery<TCampaignApplicationOpen>({
    enabled: !!campaignId && (isPro ? !!management.declined_applications_pro : !!management.declined_applications),
    isActiveTab,
    queryKey: CAMPAIGN_KEYS.qCampaignDeclinedApplications(
      campaignId,
      `${stripParamKeys(searchParams ?? "")}&pro=${isPro}`,
    ),
  });
};

export const useCampaignFeaturedContentQuery = (queryOptions?: TQueryOptions<FeaturedContentSerializer>) => {
  const campaign = useManagementCampaign();
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);

  return useGenericQuery<FeaturedContentSerializer>({
    queryKey: CAMPAIGN_KEYS.qCampaignFeaturedContent(campaign.id, dateFilter),
    enabled: !!campaign.id,
    ...queryOptions,
  });
};

export const useCampaignInfluencerInfiniteQuery = <T>({
  enabled,
  isActiveTab = true,
  queryKey,
  queryOptions,
}: {
  enabled: boolean;
  isActiveTab?: boolean;
  queryKey: QueryKey;
  queryOptions?: TInfiniteQueryOptions<T, AxiosError, TVibeQueryResult<T>>;
}) => {
  const queryClient = useQueryClient();
  const {sliceInfluencers} = useSliceInfluencerQuery();

  return useGenericInfiniteQuery<T, AxiosError, TVibeQueryResult<T>>({
    queryKey,
    enabled: enabled && (isActiveTab || queryClient.getQueryData(queryKey) !== undefined),
    select: response => {
      const firstPage = response.pages[0]?.data as TVibePaginated<T>;
      return {
        count: sumCountsByCutoffGroup(response.pageParams, response.pages),
        cutoffValue: extractCutoffFromPageParams(response.pageParams),
        frequencyDistribution: firstPage?.frequency_distribution ?? {},
        noCutoff: !!firstPage?.no_cutoff,
        results: sliceInfluencers<T>({
          hasNext: !!response.pages[response.pages.length - 1]?.data.next,
          influencers: response.pages.flatMap(page => page.data.results),
          ...queryOptions,
        }),
      };
    },
  });
};

export const useCampaignInvitedGroupsQuery = ({campaignId}: {campaignId: number}) =>
  useGenericQuery<CampaignGroupInviteSerializer[]>({
    queryKey: CAMPAIGN_KEYS.qCampaignInvitedGroups(campaignId),
    enabled: !!campaignId,
  });

export const useCampaignListQuery = (params?: {searchParams?: string}) =>
  useGenericInfiniteQueryWithCount<CampaignFeedSerializer>({
    queryKey: CAMPAIGN_KEYS.qCampaignList(params?.searchParams ?? ""),
  });

export const useCampaignManagementQuery = ({
  campaignId,
  queryOptions,
}: {
  queryOptions?: TQueryOptions<CampaignManagementSerializer>;
  campaignId: number;
}) => {
  const query = useGenericQuery<CampaignManagementSerializer>({
    queryKey: CAMPAIGN_KEYS.qCampaignManagement(campaignId),
    enabled: !!campaignId,
    ...queryOptions,
  });

  const modifiedData = !query.data
    ? undefined
    : ({
        ...query.data,
        open_applications_pro: (query.data?.open_applications_pro ?? 0) - (query.data?.renegotiations ?? 0),
      } as unknown as CampaignManagementSerializer);

  return {
    ...query,
    data: modifiedData,
  };
};

export const useCampaignManagementStatsQuery = ({
  campaignId,
  queryOptions,
}: {
  campaignId: number;
  queryOptions?: TQueryOptions<CampaignManagementStatsSerializer>;
}) =>
  useGenericQuery<CampaignManagementStatsSerializer>({
    queryKey: CAMPAIGN_KEYS.qCampaignManagementStats(campaignId),
    enabled: !!campaignId,
    ...queryOptions,
  });

export const useCampaignOpenApplicationsQuery = ({
  campaignId,
  isActiveTab,
  isPro = false,
}: {
  campaignId: number;
  isActiveTab?: boolean;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useCampaignInfluencerFilterStore(state => state.searchParams);

  return useCampaignInfluencerInfiniteQuery<TCampaignApplicationOpen>({
    enabled: !!campaignId && (isPro ? !!management.open_applications_pro : !!management.open_applications),
    isActiveTab,
    queryKey: CAMPAIGN_KEYS.qCampaignOpenApplications(campaignId, `${stripParamKeys(searchParams ?? "")}&pro=${isPro}`),
  });
};

export const useCampaignPendingInvitationsQuery = ({
  campaignId,
  isActiveTab,
  isPro = false,
}: {
  campaignId: number;
  isActiveTab?: boolean;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useCampaignInfluencerFilterStore(state => state.searchParams);

  return useCampaignInfluencerInfiniteQuery<InviteInfluencerSerializer>({
    enabled: !!campaignId && (isPro ? !!management.pending_invitations_pro : !!management.pending_invitations),
    isActiveTab,
    queryKey: CAMPAIGN_KEYS.qCampaignInvitations(campaignId, `${stripParamKeys(searchParams ?? "")}&pro=${isPro}`),
  });
};

export const useCampaignPreparationApplicationsQuery = ({
  isPackageSentPending,
  queryOptions,
}: {
  isPackageSentPending: boolean;
  queryOptions?: TInfiniteQueryOptions<
    TCampaignApplicationAccepted,
    AxiosError,
    TVibeQueryResult<TCampaignApplicationAccepted>
  >;
}) => {
  const isPreparationEnabled = useCampaignManagementStore(state => {
    const applications = state.management.preparation_applications;
    return !isPackageSentPending || !!applications?.pending;
  });
  const campaign = useManagementCampaign();
  const search = useCampaignInfluencerFilterStore(state => state.filters.search);

  return useCampaignInfluencerInfiniteQuery<TCampaignApplicationAccepted>({
    enabled: !!campaign.id && isPreparationEnabled,
    queryKey: CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id, isPackageSentPending, search),
    queryOptions,
  });
};

export const useCampaignRejectedInvitationsQuery = ({
  campaignId,
  isActiveTab,
  isPro = false,
}: {
  campaignId: number;
  isActiveTab?: boolean;
  isPro?: boolean;
}) => {
  const management = useCampaignManagementStore(state => state.management);
  const searchParams = useCampaignInfluencerFilterStore(state => state.searchParams);

  return useCampaignInfluencerInfiniteQuery<InviteInfluencerSerializer>({
    enabled: !!campaignId && (isPro ? !!management.rejected_invitations_pro : !!management.rejected_invitations),
    isActiveTab,
    queryKey: CAMPAIGN_KEYS.qCampaignRejectedInvitations(
      campaignId,
      `${stripParamKeys(searchParams ?? "")}&pro=${isPro}`,
    ),
  });
};

export const useCampaignReportingQuery = () => {
  const isAdmin = useIsAdmin();
  const campaign = useManagementCampaign();
  const isReportingEnabled = useCampaignManagementStore(state => !!state.management.reporting_applications);
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);

  return useGenericQuery<CampaignReportingSerializer>({
    queryKey: CAMPAIGN_KEYS.qCampaignReporting({campaignId: campaign.id, dateFilter}),
    enabled: !!campaign.id && (isReportingEnabled || isAdmin),
  });
};

export const useCampaignReportingApplicationsQuery = ({
  queryOptions,
  searchParam,
}: {
  queryOptions?: TInfiniteQueryOptions<TCampaignApplicationReporting>;
  searchParam?: string;
} = {}) => {
  const campaign = useManagementCampaign();
  const isReportingEnabled = useCampaignManagementStore(state => !!state.management.reporting_applications);
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);
  const ordering = useCampaignManagementStore(state => state.reportingOrdering);
  const search = useCampaignInfluencerFilterStore(state => state.filters.search);

  const params = {campaignId: campaign.id, dateFilter, ordering, searchParam: searchParam ?? search};

  return useGenericInfiniteQuery<TCampaignApplicationReporting>({
    queryKey: CAMPAIGN_KEYS.qCampaignReportingApplications(params),
    enabled: !!campaign.id && isReportingEnabled,
    select: data =>
      data.pages.flatMap(page =>
        page.data.results.map(application => ({
          ...application,
          rejected_submissions: applyFirstCarouselChildMedia(application.rejected_submissions, s => s.carousel_medias),
          instagram_media: {
            ...application.instagram_media,
            posts: applyFirstCarouselChildMedia(application.instagram_media?.posts, p => p.children) ?? [],
          } as InstagramMediaResponseSerializer,
        })),
      ),
    ...queryOptions,
  });
};

export const useDefaultCreativeSnippetsQuery = () =>
  useGenericQuery<CreativeSnippetsResponseSerializer>({
    queryKey: CAMPAIGN_KEYS.qDefaultCreativeSnippets,
    staleTime: 5 * 60 * 1000,
  });

export const useOpenCampaignsQuery = ({
  companyId: companyIdOverride,
  queryOptions,
}: {
  companyId?: number | null;
  queryOptions?: TQueryOptions<OpenCampaignSerializer[]>;
} = {}) => {
  const selectedCompanyId = useSelectedCompanyId();
  const companyId = companyIdOverride ?? selectedCompanyId;

  return useGenericQuery<OpenCampaignSerializer[]>({
    queryKey: !!companyId ? COMPANY_KEYS.qCompanyOpenCampaigns(companyId) : CAMPAIGN_KEYS.qOpenCampaigns,
    ...queryOptions,
  });
};

const applyFirstCarouselChildMedia = <T extends {media_type?: string | null}>(
  items: T[] | undefined,
  getChildren: (
    item: T,
  ) =>
    | {media_file?: string | null; media_type?: string; thumbnail_file?: string | null; video_length?: number | null}[]
    | null
    | undefined,
) =>
  items?.map(item => {
    const firstChild = getChildren(item)?.[0];

    return item.media_type === "CAROUSEL_ALBUM" && firstChild
      ? {
          ...item,
          media_file: firstChild.media_file,
          media_type: firstChild.media_type,
          thumbnail_file: firstChild.thumbnail_file,
          video_length: firstChild?.video_length,
        }
      : item;
  });
